import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { Alert, Button } from '../../components/ui'
import { keys } from '../../api/keys'
import { setInfoNeeded } from '../../api/objects-write'
import type { Listing } from '../../api/objects'
import { useSession } from '../../auth/session'
import { t } from '../../i18n'

/**
 * What this object still needs from the landlord, and the one button that says
 * it arrived.
 *
 * An advert imported from the channel is often thin — a price and a photograph
 * and nothing about the deposit — and the gap only surfaces when a client asks.
 * By then the answer is somebody scrolling back through a chat with the owner
 * for a question that was asked before. The import writes the gaps down while it
 * can still see them; this is where a person reads and clears them.
 *
 * Raising and clearing are the same field, which is why there is one control for
 * both. A screen that could show a mark it has no way to remove is how a list of
 * "waiting on the landlord" fills up with things that came back weeks ago.
 */
export function InfoNeeded({ listing, propertyId }: { readonly listing: Listing; readonly propertyId: string }) {
  const { can } = useSession()
  const client = useQueryClient()
  const [asking, setAsking] = useState(false)
  const [text, setText] = useState('')

  const save = useMutation({
    mutationFn: (needed: string) => setInfoNeeded(listing.id, needed),
    onSuccess: () => {
      setAsking(false)
      setText('')
      void client.invalidateQueries({ queryKey: keys.property(propertyId) })
    },
  })

  const editable = can('listings.write')

  if (listing.infoNeeded === '') {
    if (!editable) return null

    return asking ? (
      <div className="info-needed info-needed--asking">
        <input
          className="field__input"
          value={text}
          placeholder={t('listing.infoNeededPlaceholder')}
          onChange={(event) => {
            setText(event.target.value)
          }}
        />
        <Button
          disabled={save.isPending || text.trim() === ''}
          onClick={() => {
            save.mutate(text)
          }}
        >
          {t('listing.infoNeededSave')}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            setAsking(false)
          }}
        >
          {t('common.cancel')}
        </Button>
      </div>
    ) : (
      <Button
        variant="ghost"
        className="info-needed__raise"
        onClick={() => {
          setAsking(true)
        }}
      >
        {t('listing.infoNeededRaise')}
      </Button>
    )
  }

  return (
    <div className="info-needed">
      <Alert tone="warning">
        <div>
          <strong>{t('listing.infoNeededTitle')}</strong>
          <div className="info-needed__text">{listing.infoNeeded}</div>
          {editable ? (
            <div className="info-needed__actions">
              <Button
                variant="secondary"
                busy={save.isPending}
                onClick={() => {
                  save.mutate('')
                }}
              >
                {t('listing.infoNeededClear')}
              </Button>
            </div>
          ) : null}
        </div>
      </Alert>
      {save.isError ? <Alert tone="error">{t('listing.infoNeededFailed')}</Alert> : null}
    </div>
  )
}
